import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Car, Mail, Phone, Building2, BadgeCheck, IdCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import ContraventionsView from "@/components/common/ContraventionModule";
import { fdate, daysUntil } from "@/lib/format";
import { DeadlineBadge } from "@/components/common/Badges";
import { licenseLevel } from "@/pages/Drivers";

const Info = ({ label, value }) => (<div><dt className="text-xs uppercase tracking-wide text-[#8695AB]">{label}</dt><dd className="mt-0.5 font-medium text-[#18221F]">{value || "—"}</dd></div>);

const LicenseCard = ({ e }) => {
  const n = daysUntil(e.license_expiry);
  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="employee-license-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-[#001A3B]"><IdCard className="h-5 w-5" />Permis de conduire</h2>
        {e.license_expiry && <DeadlineBadge level={licenseLevel(e.license_expiry)}>{n < 0 ? "Expiré" : n < 30 ? `Expire dans ${n} j` : "Valide"}</DeadlineBadge>}
      </div>
      {n != null && n < 30 && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="employee-license-alert">
          <AlertTriangle className="h-4 w-4" />{n < 0 ? `Permis expiré depuis ${Math.abs(n)} jour(s).` : `Le permis expire dans ${n} jour(s) — pensez au renouvellement.`}
        </div>
      )}
      <dl className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-5">
        <Info label="N° permis" value={e.license_number} />
        <Info label="Catégories" value={e.license_categories?.length ? e.license_categories.join(" · ") : null} />
        <Info label="Délivré le" value={e.license_issue_date ? fdate(e.license_issue_date) : null} />
        <Info label="Expire le" value={e.license_expiry ? fdate(e.license_expiry) : null} />
        <Info label="Points" value={e.license_points != null && e.license_points !== "" ? `${e.license_points} / 12` : null} />
      </dl>
      <p className="mt-4 text-xs text-[#8695AB]">Le scan du permis se joint depuis la liste des conducteurs (icône trombone).</p>
    </div>
  );
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [e, setE] = useState(null);

  useEffect(() => { api.get(`/employees/${id}`).then((r) => setE(r.data)).catch((err) => toast.error(apiError(err))); }, [id]);
  if (!e) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-56 w-full rounded-2xl" /></div>;

  return (
    <div>
      <button onClick={() => nav("/drivers")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#001A3B] hover:underline" data-testid="back-to-drivers"><ArrowLeft className="h-4 w-4" />Retour aux conducteurs</button>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#001A3B]">{e.first_name} {e.last_name}</h1>
            <p className="text-sm text-[#4A5568]">{e.fonction || "—"} · {e.service || "—"} · {e.company || "—"}</p>
            {e.archived && <span className="mt-2 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800" data-testid="employee-archived-badge">Archivé{e.archived_at ? ` le ${fdate(e.archived_at)}` : ""}</span>}
          </div>
          {e.current_vehicle_id ? (
            <Link to={`/vehicles/${e.current_vehicle_id}`} className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-[#F5F7FA] px-4 py-2 text-sm font-medium text-[#001A3B] hover:bg-[#EEF2F8]" data-testid="emp-current-vehicle">
              <Car className="h-4 w-4" />{e.current_vehicle}
            </Link>
          ) : <span className="rounded-xl border border-dashed border-[#E2E8F0] px-4 py-2 text-sm text-[#8695AB]">Aucun véhicule attribué</span>}
        </div>
        <dl className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
          <Info label="Matricule" value={e.rh_id} /><Info label="Email" value={e.email} /><Info label="Téléphone" value={e.phone} /><Info label="Site" value={e.site} />
          <Info label="Statut" value={e.status} /><Info label="Arrivée" value={fdate(e.arrival_date)} />
        </dl>
      </div>

      <LicenseCard e={e} />

      <div className="mt-6">
        <Tabs defaultValue="contraventions">
          <TabsList className="mb-4">
            <TabsTrigger value="contraventions" data-testid="emp-tab-contraventions">Contraventions</TabsTrigger>
            <TabsTrigger value="affectations" data-testid="emp-tab-affectations">Historique d'affectation</TabsTrigger>
          </TabsList>
          <TabsContent value="contraventions">
            <ContraventionsView scope={{ employee_id: id }} showFilters={false} />
          </TabsContent>
          <TabsContent value="affectations">
            {(!e.assignment_history || e.assignment_history.length === 0) ? <p className="py-8 text-center text-sm text-[#8695AB]">Aucun historique</p> : (
              <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white"><table className="w-full text-sm">
                <thead><tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">{["Début", "Fin", "Site", "Service", "Statut"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                <tbody>{e.assignment_history.map((a) => (<tr key={a.id} className="border-b border-[#EDF0F5] last:border-0"><td className="px-4 py-3">{fdate(a.start_date)}</td><td className="px-4 py-3">{a.end_date ? fdate(a.end_date) : "En cours"}</td><td className="px-4 py-3">{a.site || "—"}</td><td className="px-4 py-3">{a.service || "—"}</td><td className="px-4 py-3">{a.active ? <span className="text-emerald-700">Active</span> : "Clôturée"}</td></tr>))}</tbody>
              </table></div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
